import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { RewardRedemptionState } from '../models/RewardStore';
import { useRewardStore, useRewardStoreListField } from '../stores/RewardStore';
import { arrayEqual } from '../utils/array';

import useFetchList, { FetchListFn } from './useFetchList';

const { fetchRedemptionsByOfferId, cancelRedemption } =
  useRewardStore.getState();

interface State {
  loadingTotal: number;
  shouldFetchAgain: boolean;
  hasCancelled: boolean;
}

const useCancelRedemption = (
  rewardOfferStore: string,
  rewardOfferId: string,
) => {
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const rewardRedemptionId = query.get('rewardRedemptionId');

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    shouldFetchAgain: false,
    hasCancelled: false,
  });

  const rewardRedemptionsFieldName = `${rewardOfferStore}_${rewardOfferId}`;
  const rewardRedemptionsField = useRewardStoreListField(
    'redemptions',
    rewardRedemptionsFieldName,
  );

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      fetchRedemptionsByOfferId(rewardOfferStore, rewardOfferId ?? '', cursor),
    [rewardOfferId, rewardOfferStore],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    rewardRedemptionsField,
    fetchRewardRedemptions,
  );

  useEffect(() => {
    if (state.shouldFetchAgain) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllRewardRedemptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
            shouldFetchAgain: false,
          }));
        });
    }
  }, [fetchAllRewardRedemptions, rewardOfferId, state.shouldFetchAgain]);

  const rewardRedemptions = useRewardStore((rewardState) => {
    return rewardState.getRedemptions(rewardRedemptionsFieldName);
  }, arrayEqual(shallow));

  // const completedRedemption = useMemo(() => {
  //   return rewardRedemptions.find(
  //     // TODO: may need to check expiered if yearly
  //     (item) => item.state === RewardRedemptionState.Fulfilled,
  //   );
  // }, [rewardRedemptions]);

  const payingRedemption = useMemo(() => {
    return rewardRedemptions.find(
      (item) => item.state === RewardRedemptionState.Paying,
    );
  }, [rewardRedemptions]);

  useEffect(() => {
    if (
      (rewardRedemptionId !== null ||
        payingRedemption?.rewardRedemptionId !== undefined) &&
      !state.hasCancelled
    ) {
      const idToCancel: string =
        rewardRedemptionId || payingRedemption?.rewardRedemptionId || '';

      cancelRedemption(rewardOfferStore, idToCancel)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        });
    }
  }, [
    payingRedemption?.rewardRedemptionId,
    rewardRedemptionId,
    rewardOfferStore,
    state.hasCancelled,
  ]);

  const fetchAgain = useCallback(() => {
    setState((s) => ({ ...s, shouldFetchAgain: true }));
  }, []);

  return fetchAgain;
};

export default useCancelRedemption;
